import styled, { css, type DefaultTheme } from 'styled-components';
import { breakpointDown, breakpointUp } from '/shared/styled/Breakpoints';
import Colours from '/shared/styled/Colours';
import { baseFontWeight } from '/shared/styled/GlobalStyle/GlobalStyle';

interface StyledFormLabelProps {
  theme?: DefaultTheme;
  $isRow?: boolean;
  $isColumn?: boolean;
  $noStretch?: boolean;
  $greyLabel?: boolean;
  $blackLabel?: boolean;
  $noWrap?: boolean;
  $slightlyMoreTopPadding?: boolean;
  $slightlyMoreBottomPadding?: boolean;
  $moreTopPadding?: boolean;
  $moreBottomPadding?: boolean;
  $noBottomPadding?: boolean;
  $larger?: boolean;
  $checkbox?: boolean;
  $alignLabelTop?: boolean;
  $alignLabelStart?: boolean;
  $lightFontWeight?: boolean;
  $negativeMargin?: boolean;
  $splitLabelPadding?: string | null;
  $alignLabelMiddle?: boolean;
  $borderTop?: string | null;
  $isLeftAligned?: boolean;
  $isSmallText?: boolean;
  $outdent?: boolean;
  $outdentLarger?: boolean;
  $business?: boolean;
  $generator?: boolean;
  $maxWidth?: string;
  $maxSpanWidth?: string;
  $minSpanWidth?: string;
}

function isRowStyle({ $isRow = false }: StyledFormLabelProps) {
  function outdentStyle({ $outdent, $outdentLarger = false }: StyledFormLabelProps) {
    if ($outdentLarger) {
      return css`
        align-items: center;
        ${breakpointDown('mobile')} {
          flex-direction: column;

          > span {
            margin: 0 0 1rem 2px;
            font-size: 1.3333rem;
          }
        }
        ${breakpointUp('mobile')} {
          > span {
            width: 15rem;
            min-width: 15rem;
            font-size: 1.3333rem;
          }
        }
      `;
    }
    if ($outdent) {
      return css`
        ${breakpointDown('mobile')} {
          flex-direction: column;
          align-items: flex-start;

          > span {
            margin: 0 0 1rem 2px;
            font-size: 1.3333rem;
          }
        }
        ${breakpointUp('mobile')} {
          > span {
            position: absolute;
            margin-left: -5rem;
            font-size: 1.3333rem;
          }
        }
      `;
    }
    return css`
      >span {
        font-size: 1.25rem;
      }
    `;
  }
  function businessStyle({ $business }: StyledFormLabelProps) {
    if ($business) {
      return css`
        > * {
          flex: 4;
        }

        > span {
          margin-right: 1rem;
          margin-left: 0;
          flex: 3;
        }
      `;
    }
  }
  function generatorStyle({ $generator }: StyledFormLabelProps) {
    if ($generator) {
      return css`
        > * {
          flex: 6;
        }

        > span {
          margin-right: 1rem;
          margin-left: 0;
        }
      `;
    }
  }
  if ($isRow) {
    return css`
      flex-direction: row;
      width: 100%;

      > span {
        display: block;
        margin: 0 1rem 0 0.5rem;
        font-size: 0.9333rem;
      }
      ${outdentStyle}
      ${businessStyle}
      ${generatorStyle}
    `;
  }
}

function isColumnStyle({ $isColumn = false }: StyledFormLabelProps) {
  if ($isColumn) {
    return css`
      flex-direction: column;
    `;
  }
}

function noStretchStyle({ $noStretch = false }: StyledFormLabelProps) {
  if ($noStretch) {
    return css`
      width: auto;
    `;
  }
  return css`
    width: 100%;
  `;
}

function greyLabelStyle({ $greyLabel = false }: StyledFormLabelProps) {
  if ($greyLabel) {
    return css`
      > span {
        color: ${Colours.grey};
      }
    `;
  }
}

function blackLabelStyle({ $blackLabel = false }: StyledFormLabelProps) {
  if ($blackLabel) {
    return css`
      > span {
        color: ${Colours.black};

        a {
          color: ${Colours.black};
        }
      }
    `;
  }
}

function noWrapStyle({ $noWrap = false }: StyledFormLabelProps) {
  if ($noWrap) {
    return css`
      white-space: nowrap;
    `;
  }
}

function slightlyMoreTopPaddingStyle({ $slightlyMoreTopPadding = false }: StyledFormLabelProps) {
  if ($slightlyMoreTopPadding) {
    return css`
      padding-top: 1.25rem;
    `;
  }
}

function slightlyMoreBottomPaddingStyle({ $slightlyMoreBottomPadding = false }: StyledFormLabelProps) {
  if ($slightlyMoreBottomPadding) {
    return css`
      padding-bottom: 1.25rem;
    `;
  }
}

function moreTopPaddingStyle({ $moreTopPadding = false }: StyledFormLabelProps) {
  if ($moreTopPadding) {
    return css`
      padding-top: 1.75rem;
    `;
  }
}

function moreBottomPaddingStyle({ $moreBottomPadding = false }: StyledFormLabelProps) {
  if ($moreBottomPadding) {
    return css`
      padding-bottom: 1.75rem;
    `;
  }
}

function noBottomPaddingStyle({ $noBottomPadding = false }: StyledFormLabelProps) {
  if ($noBottomPadding) {
    return css`
      padding-bottom: 0;
    `;
  }
}

function largerStyle({ $larger = false }: StyledFormLabelProps) {
  if ($larger) {
    return css`
      > span:first-of-type {
        font-size: 1.25rem;
      }
    `;
  }
}

function checkboxStyle({ $checkbox = false }: StyledFormLabelProps) {
  if ($checkbox) {
    return css`
      flex-direction: row;
      align-items: center;

      > span {
        display: block;
        font-size: 0.9333rem;
        /* line-height: 1.25; */
        padding-left: 1rem;
        max-width: 22rem;
        font-weight: normal;
        margin: 0;
        color: ${Colours.grey};
        line-height: 1.8575;

        a {
          text-decoration: underline;
          color: ${Colours.grey};
        }
      }

      > p {
        padding-left: 1rem;
      }
    `;
  }
}

function alignLabelTopStyle({ $alignLabelTop = false }: StyledFormLabelProps) {
  if ($alignLabelTop) {
    return css`
      align-items: end;
    `;
  }
}

function alignLabelStartStyle({ $alignLabelStart = false }: StyledFormLabelProps) {
  if ($alignLabelStart) {
    return css`
      align-items: flex-start;
    `;
  }
}

function lightFontWeightStyle({ theme, $lightFontWeight = false }: StyledFormLabelProps) {
  if ($lightFontWeight && theme?.target.isBusiness === false) {
    return css`
      font-weight: 300 !important;
    `;
  }
}

function negativeMarginStyle({ $negativeMargin = false }: StyledFormLabelProps) {
  if ($negativeMargin) {
    return css`
      > span {
        margin-right: -1rem;
      }
    `;
  }
}

function moreLabelPaddingStyle({ $splitLabelPadding = null }: StyledFormLabelProps) {
  if ($splitLabelPadding) {
    return css`
      > div {
        display: flex;
        justify-content: flex-end;

        > div {
          width: ${$splitLabelPadding};
        }
      }
    `;
  }
}

function alignLabelMiddleStyle({ $alignLabelMiddle = false }: StyledFormLabelProps) {
  if ($alignLabelMiddle) {
    return css`
        align-items: center;
    `;
  }
}

function borderTopStyle({ $borderTop = null }: StyledFormLabelProps) {
  if ($borderTop) {
    return css`
      border-top: ${$borderTop};
    `;
  }
}

function isLeftAligned({ $isLeftAligned }: StyledFormLabelProps) {
  if ($isLeftAligned) {
    return css`
      justify-content: flex-start !important;
    `;
  }
}

function isSmallTextStyle({ $isSmallText }: StyledFormLabelProps) {
  if ($isSmallText) {
    return css`
      font-size: 12px;
    `;
  }
}

export default styled.label<StyledFormLabelProps>`
  display: flex;
  padding: 0.25rem 0 0.75rem;
  flex-direction: column;
  white-space: normal;
  max-width: ${({ $maxWidth }) => $maxWidth || 'auto'};
  ${breakpointDown('mobile')} {
    padding-bottom: 1rem;
  }

  > span {
    max-width: ${({ $maxSpanWidth }) => $maxSpanWidth || 'none'};
    min-width: ${({ $minSpanWidth }) => $minSpanWidth || 'auto'};
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.0666rem;
    line-height: 1.5;
    color: ${Colours.black};
    white-space: normal;
  }

  > small {
    color: ${Colours.black};
    font-size: 0.93333rem;
    padding: 0.5rem 0;
    ${baseFontWeight}
  }

  .lbl-info-icon {
    background: ${Colours.green};
    border: none;
    width: 21px;
    height: 21px;
    border-radius: 50%;
    margin-right: 8px;
    color: ${Colours.white};
    cursor: pointer;
    font-size: 18px;
    margin-left: 8px;
  }

  h3 {
    padding-left: 25px;
    font-size: 20px;
    line-height: 30px;
    letter-spacing: 0.2px;
    font-weight: 400;

    b {
      font-weight: 500;
    }
  }

  hr {
    width: 100%;
    margin: 1rem 0 3rem;
    background-color: ${Colours.greyLight9};
    height: 2px;
    border: none;
  }
  ${isRowStyle}
  ${isColumnStyle}
  ${noStretchStyle}
  ${greyLabelStyle}
  ${noWrapStyle}
  ${slightlyMoreTopPaddingStyle}
  ${slightlyMoreBottomPaddingStyle}
  ${moreTopPaddingStyle}
  ${moreBottomPaddingStyle}
  ${moreLabelPaddingStyle}
  ${noBottomPaddingStyle}
  ${largerStyle}
  ${checkboxStyle}
  ${alignLabelTopStyle}
  ${alignLabelStartStyle}
  ${lightFontWeightStyle}
  ${negativeMarginStyle}
  ${borderTopStyle}
  ${blackLabelStyle}
  ${alignLabelMiddleStyle}
  ${isLeftAligned}
  ${isSmallTextStyle}
`;
